// S24: Admin Audit Logs Page
import { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Card,
  Button,
  Drawer,
  Typography,
  Descriptions,
  Alert,
  message,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { PageHeader } from '../components/PageHeader';
import {
  auditLogEndpoints,
  type AuditLog,
  type AuditLogDetail,
  type AuditLogFilters,
} from '../api/auditLogEndpoints';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export function AdminAuditLogs() {
  const [loading, setLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  // Filter options
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);

  // Filters
  const [eventFilter, setEventFilter] = useState<string | undefined>(undefined);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | undefined>(undefined);
  const [actorTypeFilter, setActorTypeFilter] = useState<'user' | 'system' | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [entityIdSearch, setEntityIdSearch] = useState('');

  // Detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchEventTypes();
    fetchEntityTypes();
    fetchAuditLogs();
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [pagination.current, pagination.pageSize, eventFilter, entityTypeFilter, actorTypeFilter, dateRange]);

  const fetchEventTypes = async () => {
    try {
      const data = await auditLogEndpoints.getEventTypes();
      setEventTypes(data.events);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const fetchEntityTypes = async () => {
    try {
      const data = await auditLogEndpoints.getEntityTypes();
      setEntityTypes(data.entity_types);
    } catch (error) {
      console.error('Failed to fetch entity types:', error);
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const filters: AuditLogFilters = {
        page: pagination.current,
        per: pagination.pageSize,
        event: eventFilter,
        entity_type: entityTypeFilter,
        actor_type: actorTypeFilter,
        entity_id: entityIdSearch || undefined,
      };

      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.from = dateRange[0].toISOString();
        filters.to = dateRange[1].toISOString();
      }

      const data = await auditLogEndpoints.getAuditLogs(filters);

      setAuditLogs(data.audit_logs || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total_count || 0,
      }));
    } catch (error: any) {
      console.error('Failed to fetch audit logs:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load audit logs';
      message.error(errorMessage);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: newPagination.total,
    });
  };

  const handleReset = () => {
    setEventFilter(undefined);
    setEntityTypeFilter(undefined);
    setActorTypeFilter(undefined);
    setDateRange(null);
    setEntityIdSearch('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetails = async (logId: string) => {
    setDetailLoading(true);
    setDrawerOpen(true);
    try {
      const data = await auditLogEndpoints.getAuditLog(logId);
      setSelectedLog(data.audit_log);
    } catch (error) {
      console.error('Failed to fetch audit log details:', error);
      message.error('Failed to load audit log details');
      setDrawerOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const getEventColor = (event: string | null | undefined): string => {
    if (!event) return 'default';
    
    if (event.includes('failed') || event.includes('rejected') || event.includes('expired')) {
      return 'red';
    }
    if (event.includes('approved') || event.includes('succeeded') || event.includes('completed') || event.includes('consumed')) {
      return 'green';
    }
    if (event.includes('started') || event.includes('scheduled') || event.includes('requested')) {
      return 'blue';
    }
    return 'default';
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Time',
      dataIndex: 'at',
      key: 'at',
      width: 180,
      render: (at: string) => dayjs(at).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(a.at).unix() - dayjs(b.at).unix(),
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      width: 250,
      render: (event: string) => <Tag color={getEventColor(event)}>{event}</Tag>,
    },
    {
      title: 'Actor',
      key: 'actor',
      width: 200,
      render: (_, record) => (
        <Space>
          {record.actor_type === 'system' ? (
            <Tag icon={<RobotOutlined />} color="purple">
              System
            </Tag>
          ) : (
            <Tag icon={<UserOutlined />} color="blue">
              {record.actor_name || 'User'}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 200,
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{record.entity_type}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.entity_id ? record.entity_id.substring(0, 8) : 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Metadata Preview',
      key: 'metadata',
      render: (_, record) => (
        <Space size={[0, 4]} wrap>
          {record.metadata_preview.map((item, idx) => (
            <Tag key={idx}>
              <Text strong>{item.key}:</Text> {item.value}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 150,
      render: (ip: string) => <Text code>{ip || 'N/A'}</Text>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record.id)}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Audit Logs"
        description="View system activity and compliance audit trail"
      />

      <Card style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            title="Audit Log Information"
            description="This page displays all critical system actions including invites, profit entries, allocations, withdrawals, and settlements. Use filters to find specific events."
            type="info"
            showIcon
          />

          <Space wrap>
            <Select
              placeholder="Filter by Event"
              style={{ width: 250 }}
              value={eventFilter}
              onChange={setEventFilter}
              allowClear
              showSearch
            >
              {eventTypes.map(event => (
                <Select.Option key={event} value={event}>
                  {event}
                </Select.Option>
              ))}
            </Select>

            <Select
              placeholder="Filter by Entity Type"
              style={{ width: 200 }}
              value={entityTypeFilter}
              onChange={setEntityTypeFilter}
              allowClear
            >
              {entityTypes.map(type => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </Select>

            <Select
              placeholder="Filter by Actor"
              style={{ width: 150 }}
              value={actorTypeFilter}
              onChange={setActorTypeFilter}
              allowClear
            >
              <Select.Option value="user">User</Select.Option>
              <Select.Option value="system">System</Select.Option>
            </Select>

            <Input
              placeholder="Entity ID"
              value={entityIdSearch}
              onChange={e => setEntityIdSearch(e.target.value)}
              onPressEnter={fetchAuditLogs}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />

            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              style={{ width: 250 }}
            />

            <Button icon={<SearchOutlined />} type="primary" onClick={fetchAuditLogs}>
              Search
            </Button>

            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </Space>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={auditLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} audit logs`,
            pageSizeOptions: ['25', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Detail Drawer */}
      <Drawer
        title="Audit Log Details"
        placement="right"
        size={720}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        loading={detailLoading}
        mask={false}
        styles={{ 
          body: { backgroundColor: '#141414' },
          header: { backgroundColor: '#1f1f1f', borderBottom: '1px solid #303030' }
        }}
      >
        {selectedLog && (
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions title="Event Information" bordered column={1}>
              <Descriptions.Item label="Event">
                <Tag color={getEventColor(selectedLog.event)}>{selectedLog.event}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Time">
                {dayjs(selectedLog.at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="ID">
                <Text code>{selectedLog.id}</Text>
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="Actor" bordered column={1}>
              <Descriptions.Item label="Type">
                {selectedLog.actor_type === 'system' ? (
                  <Tag icon={<RobotOutlined />} color="purple">
                    System
                  </Tag>
                ) : (
                  <Tag icon={<UserOutlined />} color="blue">
                    User
                  </Tag>
                )}
              </Descriptions.Item>
              {selectedLog.actor.type === 'user' && (
                <>
                  <Descriptions.Item label="Name">{selectedLog.actor.full_name}</Descriptions.Item>
                  <Descriptions.Item label="Email">{selectedLog.actor.email}</Descriptions.Item>
                  <Descriptions.Item label="Role">
                    <Tag>{selectedLog.actor.role}</Tag>
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            <Descriptions title="Entity" bordered column={1}>
              <Descriptions.Item label="Type">{selectedLog.entity_type}</Descriptions.Item>
              <Descriptions.Item label="ID">
                <Text code>{selectedLog.entity_id || 'N/A'}</Text>
              </Descriptions.Item>
              {selectedLog.entity && !selectedLog.entity.deleted && (
                <Descriptions.Item label="Details">
                  <pre style={{ margin: 0, fontSize: '12px' }}>
                    {JSON.stringify(selectedLog.entity, null, 2)}
                  </pre>
                </Descriptions.Item>
              )}
              {selectedLog.entity?.deleted && (
                <Descriptions.Item label="Status">
                  <Tag color="red">Entity Deleted</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Card title="Full Metadata" size="small">
              <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto', maxHeight: '400px' }}>
                {JSON.stringify(selectedLog.metadata, null, 2)}
              </pre>
            </Card>

            <Descriptions title="Request Information" bordered column={1}>
              <Descriptions.Item label="IP Address">
                <Text code>{selectedLog.ip_address || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="User Agent">
                <Text style={{ fontSize: '11px', wordBreak: 'break-word' }}>
                  {selectedLog.user_agent || 'N/A'}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}
      </Drawer>
    </div>
  );
}
